import type { RosterEntry } from '@league/shared/contract';
import { effectivePace, PACE_OPTIONS, paceLabel, type PlayingPace } from '@league/shared/schemas';

import { leagueDate } from '../../lib/when';

/** Roster actions concern players; payment information belongs to Registrations. */
export type LoginKind = 'signed_in' | 'invited' | 'not_invited' | 'no_login';

/**
 * Whether she can get into the app. A director who also plays signs in with her own login, which
 * is signed in as far as this column cares. `no_account` cannot be invited from here, so it says so
 * rather than offering an invitation that would do nothing.
 */
export function loginStatus(entry: RosterEntry, zone: string): { kind: LoginKind; label: string } {
  switch (entry.account) {
    case 'signed_in':
    case 'staff_login':
      return { kind: 'signed_in', label: 'Signed in' };
    case 'invited':
      return { kind: 'invited', label: entry.invitedAt ? `Invited ${leagueDate(entry.invitedAt, zone)}` : 'Invited' };
    case 'not_invited':
      return { kind: 'not_invited', label: 'Not invited' };
    default:
      return { kind: 'no_login', label: 'No login' };
  }
}

/**
 * The pace seating uses: the director's setting for this league, else the player's own choice.
 * "Not sure yet" is the PLAYER's wording when she registers; to a director it is "Not set".
 */
export function paceText(entry: RosterEntry): string {
  const pace = effectivePace(entry.player.preferredPace ?? null, entry.enrollment.paceOverride ?? null);
  return pace === 'unknown' ? 'Not set' : paceLabel(pace);
}

/**
 * THE ROW'S PACE OF PLAY DROPDOWN — owner, 2026-10-08: the director sets each player's pace for
 * her league on the row, and it never touches what the player chose in her own profile.
 *
 * The choices are the three paces, plus "Not set" while the player has not chosen one. Picking the
 * player's OWN preference clears the league setting, so she follows her profile again; anything
 * else is stored as this league's setting.
 */
export function paceChoices(entry: RosterEntry): { value: PlayingPace; label: string }[] {
  const chosen = PACE_OPTIONS.filter((option) => option.value !== 'unknown').map((option) => ({ value: option.value as PlayingPace, label: option.label }));
  /* While the PLAYER has not chosen, "Not set" stays on the list: it is how a league setting is undone. */
  const own = entry.player.preferredPace ?? 'unknown';
  return own === 'unknown' ? [{ value: 'unknown', label: 'Not set' }, ...chosen] : chosen;
}

/** What to store when the director picks a pace: null means "follow the player's own choice". */
export function paceOverrideFor(entry: RosterEntry, picked: PlayingPace): PlayingPace | null {
  const own = entry.player.preferredPace ?? 'unknown';
  return picked === own ? null : picked;
}

/** The row's invitation button: Invite before any, Resend until she signs in, then nothing. */
/*
  ⚠ TO THIS LEAGUE, AND ALWAYS AGAIN — owner, 2026-10-15. An invitation is to a league, so somebody
  who already signs in is invited too (she gets the league's invitation, not a password link), and
  any invitation can be re-sent: "if a player tells a director that they never received an email, we
  should be able to resend it". Only a director who plays, or a record with no login, has nothing.
*/
export function inviteAction(entry: RosterEntry, _zone?: string): 'invite' | 'resend' | null {
  if (entry.account === 'staff_login' || entry.account === 'no_account' || !entry.player.email) return null;
  return entry.invitedAt ? 'resend' : 'invite';
}

/** The confirmation's wording: the question, then where she goes. */
export const REMOVE_NOTE = 'They’ll move to Removed players, where you can put them back.';
export function removeQuestion(name: string): string {
  return `Remove ${name} from this league?`;
}

/** The same confirmation, for several players at once. */
export function removeManyQuestion(count: number): string {
  if (count === 0) return 'None of these players can be removed.';
  return count === 1 ? 'Remove 1 player from this league?' : `Remove ${count} players from this league?`;
}
export function removeManyNote(count: number): string {
  if (count === 0) return '';
  return count === 1 ? REMOVE_NOTE : 'They’ll move to Removed players, where you can put them back.';
}

/** The selection bar's count. */
export function selectedLabel(count: number): string {
  return `${count} ${count === 1 ? 'player' : 'players'} selected`;
}
